<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { uniqBy } from 'lodash'

import { useSnackbarStore } from '@/store/snackbar'
import { $createSignComment } from '@/api/signs'
import {
  $createChangeRequestApprovalComment,
  $createChangeRequestComment,
  $createChangeRequestSubtaskComment,
} from '@/api/changeRequests'
import { $bulkCreateChatMessages, $createChatMessage } from '@/api/global-chat'
import { $userList } from '@/api/users'
import { $getTeamLists } from '@/api/settings'

import type { TagListUser } from '@/models/user'
import type { ChatMessage } from '@/models/global/GlobalChat'

const snackbarStore = useSnackbarStore()

interface TeamListTagUser {
  id: number
  display_name: string
  email: string
}

interface TeamListItem {
  id: number
  name: string
  users: number[]
  users_data: TeamListTagUser[]
  created?: string
  modified?: string
}

interface IProps {
  isGlobalChat?: boolean
  chatItemId?: number | string | Array<number | string> | null
  chatItemType?: 'general' | 'wayfinding' | 'branding' | 'kop' | 'change_request' | 'change_request_approval' |
    'change_request_subtask' | 'finance_manual_entry' | 'waiting_room_file' |
    'file_management_file' | 'file_management_in_waiting_room_file'

  isSignComment?: boolean
  signId?: number | null

  changeRequestId?: number | string | null | undefined
  changeRequestApprovalId?: number | string | null | undefined
  changeRequestSubtaskId?: number | string | null | undefined

  placeholder?: string

  canAddComment?: boolean

  customMessageInputId?: string

  withTeamLists?: boolean
  teamListsPerPage?: number
}

const props = withDefaults(defineProps<IProps>(), {
  isGlobalChat: false,
  chatItemId: null,
  chatItemType: 'general',

  isSignComment: false,
  signId: null,

  changeRequestId: null,
  changeRequestApprovalId: null,
  changeRequestSubtaskId: null,

  placeholder: 'Write a comment...',

  canAddComment: true,

  customMessageInputId: 'activity-timeline-comment-custom-input',

  withTeamLists: true,
  teamListsPerPage: 10,
})

interface Emit {
  (e: 'onAddComment'): void
}

const emit = defineEmits<Emit>()

const rootRef = ref<HTMLDivElement | null>(null)
const editableDiv = ref<HTMLDivElement | null>(null)

const message = ref<string>('')
const isFocused = ref<boolean>(false)

const menuOpen = ref<boolean>(false)
const mentionQuery = ref<string>('')
const activeMentionRange = ref<Range | null>(null)

const activeTab = ref<'users' | 'team_lists'>('users')

const users = ref<TagListUser[]>([])
const usersCurrentPage = ref<number>(1)
const usersPerPage: number = 10
const usersSearchStr = ref<string>('')
const isNextUsersPageExist = ref<boolean>(false)
const usersLoading = ref<boolean>(false)

const teamLists = ref<TeamListItem[]>([])
const teamListsCurrentPage = ref<number>(1)
const teamListsSearchStr = ref<string>('')
const teamListsCount = ref<number>(0)
const teamListsLoading = ref<boolean>(false)
const selectedTeamList = ref<TeamListItem | null>(null)
const selectedTeamListUserIds = ref<number[]>([])
let latestUsersRequest = 0
let latestTeamListsRequest = 0

const selectedUsersFullList = ref<TagListUser[]>([])

const addBtnLoading = ref<boolean>(false)

const isMenuInteracting = ref<boolean>(false)

const MESSAGE_TYPE_MAP: Record<NonNullable<IProps['chatItemType']>, ChatMessage['message_type']> = {
  general: 1,
  wayfinding: 2,
  branding: 3,
  kop: 4,
  change_request: 5,
  change_request_approval: 6,
  change_request_subtask: 7,
  finance_manual_entry: 8,
  waiting_room_file: 9,
  file_management_file: 10,
  file_management_in_waiting_room_file: 11,
}

const FOREIGN_KEY_MAP = {
  wayfinding: 'sign',
  branding: 'line_item',
  kop: 'kop_item',
  change_request: 'change_request',
  change_request_approval: 'change_request_approval',
  change_request_subtask: 'change_request_subtask',
  finance_manual_entry: 'finance_manual_entry',
  waiting_room_file: 'waiting_room_file',
  file_management_file: 'file_management_file',
  file_management_in_waiting_room_file: 'file_management_file',
} as const

const inputType = computed(() => {
  if (props.isGlobalChat) {
    return 'globalChat'
  }
  if (props.changeRequestId) {
    return 'cr'
  }
  if (props.changeRequestApprovalId) {
    return 'crApproval'
  }
  if (props.changeRequestSubtaskId) {
    return 'crSubtask'
  }

  return 'sign'
})

const selectedTeamListUsers = computed<TeamListTagUser[]>(() => {
  return selectedTeamList.value?.users_data ?? []
})

const isAllSelectedInTeamList = computed<boolean>(() => {
  const ids = selectedTeamListUsers.value.map(user => user.id)

  if (!ids.length) {
    return false
  }

  return ids.every(id => selectedTeamListUserIds.value.includes(id))
})

const isIndeterminateSelectedInTeamList = computed<boolean>(() => {
  const ids = selectedTeamListUsers.value.map(user => user.id)
  const selectedCount = ids.filter(id => selectedTeamListUserIds.value.includes(id)).length

  return selectedCount > 0 && selectedCount < ids.length
})

const normalizeEditorText = (text: string): string => {
  return text
    .replace(/\u200B/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\r/g, '')
}

const syncMessageFromDom = () => {
  message.value = normalizeEditorText(editableDiv.value?.innerText || '')
}

const focusEditable = async () => {
  await nextTick()
  editableDiv.value?.focus()
}

const getSelectionRange = (): Range | null => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return null
  }

  const range = selection.getRangeAt(0)
  if (!editableDiv.value?.contains(range.commonAncestorContainer)) {
    return null
  }

  return range
}

const setCaretAt = (node: Node, offset: number) => {
  const selection = window.getSelection()
  if (!selection) {
    return
  }

  const range = document.createRange()
  range.setStart(node, offset)
  range.collapse(true)

  selection.removeAllRanges()
  selection.addRange(range)
}

const setCaretAfterNode = (node: Node) => {
  const selection = window.getSelection()
  if (!selection) {
    return
  }

  const range = document.createRange()
  range.setStartAfter(node)
  range.collapse(true)

  selection.removeAllRanges()
  selection.addRange(range)
}

const insertLineBreak = () => {
  const range = getSelectionRange()
  if (!range) {
    return
  }

  range.deleteContents()

  const br = document.createElement('br')
  const textNode = document.createTextNode('')

  range.insertNode(textNode)
  range.insertNode(br)

  setCaretAt(textNode, 0)
  syncMessageFromDom()
  handleCaretPosition()
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault()
    insertLineBreak()
  }
}

type TextSegment = {
  node: Text
  start: number
  end: number
}

const buildTextModel = (root: HTMLElement): {
  text: string
  segments: TextSegment[]
} => {
  const segments: TextSegment[] = []
  let fullText = ''
  let currentIndex = 0

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const textNode = node as Text
      const value = textNode.textContent || ''

      if (value.length) {
        segments.push({
          node: textNode,
          start: currentIndex,
          end: currentIndex + value.length,
        })
        fullText += value
        currentIndex += value.length
      }

      return
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return
    }

    const element = node as HTMLElement

    if (element.tagName === 'BR') {
      fullText += '\n'
      currentIndex += 1
      return
    }

    Array.from(element.childNodes).forEach(walk)
  }

  Array.from(root.childNodes).forEach(walk)

  return {
    text: fullText,
    segments,
  }
}

const getCaretTextOffset = (): number | null => {
  if (!editableDiv.value) {
    return null
  }

  const range = getSelectionRange()
  if (!range) {
    return null
  }

  const clonedRange = range.cloneRange()
  clonedRange.selectNodeContents(editableDiv.value)
  clonedRange.setEnd(range.endContainer, range.endOffset)

  return clonedRange.toString().length
}

const getRangeFromTextOffsets = (startOffset: number, endOffset: number): Range | null => {
  if (!editableDiv.value) {
    return null
  }

  const { segments } = buildTextModel(editableDiv.value)

  const findPoint = (offset: number): { node: Text, offset: number } | null => {
    for (const segment of segments) {
      if (offset >= segment.start && offset <= segment.end) {
        return {
          node: segment.node,
          offset: offset - segment.start,
        }
      }
    }

    return null
  }

  const startPoint = findPoint(startOffset)
  const endPoint = findPoint(endOffset)

  if (!startPoint || !endPoint) {
    return null
  }

  const range = document.createRange()
  range.setStart(startPoint.node, startPoint.offset)
  range.setEnd(endPoint.node, endPoint.offset)

  return range
}

const getCurrentMentionContext = (): {
  query: string
  range: Range
} | null => {
  if (!editableDiv.value) {
    return null
  }

  const caretOffset = getCaretTextOffset()
  if (caretOffset === null) {
    return null
  }

  const { text } = buildTextModel(editableDiv.value)

  let start = caretOffset
  while (start > 0 && !/\s/.test(text[start - 1])) {
    start--
  }

  let end = caretOffset
  while (end < text.length && !/\s/.test(text[end])) {
    end++
  }

  const word = text.slice(start, end)

  if (!word.startsWith('@')) {
    return null
  }

  const mentionRange = getRangeFromTextOffsets(start, end)
  if (!mentionRange) {
    return null
  }

  return {
    query: word.slice(1),
    range: mentionRange,
  }
}

const resetUsersState = () => {
  latestUsersRequest += 1
  usersLoading.value = false
  users.value = []
  usersCurrentPage.value = 1
  usersSearchStr.value = ''
  isNextUsersPageExist.value = false
}

const resetTeamListsState = () => {
  latestTeamListsRequest += 1
  teamListsLoading.value = false
  teamLists.value = []
  teamListsCurrentPage.value = 1
  teamListsSearchStr.value = ''
  teamListsCount.value = 0
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []
}

const getWaitingRoomTagFilters = (): Record<string, string> => {
  const chatItemId = Array.isArray(props.chatItemId)
    ? props.chatItemId[0]
    : props.chatItemId

  if (!props.isGlobalChat) return {}
  if (props.chatItemType === 'waiting_room_file') {
    return { tag_list_waiting_room_file_id: String(chatItemId ?? '') }
  }
  if (props.chatItemType === 'file_management_in_waiting_room_file') {
    return { tag_list_file_management_in_waiting_room_file_id: String(chatItemId ?? '') }
  }
  return {}
}

const getUsers = async () => {
  const requestId = ++latestUsersRequest
  const contextKey = JSON.stringify(getWaitingRoomTagFilters())
  usersLoading.value = true

  const filters = {
    get_all: true,  // Get all users, regardless of role
    tag_list: true, // Fields: 'id', 'display_name', 'email'
    search: usersSearchStr.value,
    per_page: usersPerPage,
    page: usersCurrentPage.value,
    ...getWaitingRoomTagFilters(),
  } as {
    get_all: boolean
    tag_list: boolean
    search: string
    per_page: number
    page: number
    tag_list_sign_id?: number
    tag_list_line_item_id?: number
    tag_list_kop_item_id?: number
    tag_list_waiting_room_file_id?: string
    tag_list_file_management_in_waiting_room_file_id?: string
  }

  const selectedChatItemId = Array.isArray(props.chatItemId)
    ? props.chatItemId[0]
    : props.chatItemId

  if (inputType.value === 'globalChat' && selectedChatItemId) {
    if (props.chatItemType === 'wayfinding') {
      filters.tag_list_sign_id = selectedChatItemId
    }
    else if (props.chatItemType === 'branding') {
      filters.tag_list_line_item_id = selectedChatItemId
    }
    else if (props.chatItemType === 'kop') {
      filters.tag_list_kop_item_id = selectedChatItemId
    }
  }

  try {
    const res = await $userList(filters)
    if (requestId !== latestUsersRequest || contextKey !== JSON.stringify(getWaitingRoomTagFilters())) return

    // Append the new users to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    users.value = uniqBy([...users.value, ...res.data.results], 'id')

    isNextUsersPageExist.value = !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    if (requestId === latestUsersRequest) usersLoading.value = false
  }
}

const getTeamLists = async () => {
  const requestId = ++latestTeamListsRequest
  const contextKey = JSON.stringify(getWaitingRoomTagFilters())
  teamListsLoading.value = true

  try {
    const requestParams: Record<string, any> = {
      page: teamListsCurrentPage.value,
      per_page: props.teamListsPerPage,
      name__icontains: teamListsSearchStr.value || undefined,
      is_tag_user_data: true,
      ...getWaitingRoomTagFilters(),
    }

    const { data } = await $getTeamLists(requestParams)
    if (requestId !== latestTeamListsRequest || contextKey !== JSON.stringify(getWaitingRoomTagFilters())) return

    teamLists.value = teamListsCurrentPage.value === 1
      ? data.results
      : [...teamLists.value, ...data.results]

    teamListsCount.value = data.count
  }
  catch (e) {
    console.error(e)
  }
  finally {
    if (requestId === latestTeamListsRequest) teamListsLoading.value = false
  }
}

const onLoadMoreUsers = async () => {
  usersCurrentPage.value += 1
  await getUsers()
}

const onLoadMoreTeamLists = async () => {
  teamListsCurrentPage.value += 1
  await getTeamLists()
}

const handleUserSearch = useDebounceFn((searchStr: string) => {
  if (activeTab.value !== 'users') {
    return
  }

  users.value = []
  usersCurrentPage.value = 1
  usersSearchStr.value = searchStr
  getUsers()
}, 300)

const handleTeamListsSearch = useDebounceFn((searchStr: string) => {
  if (activeTab.value !== 'team_lists') {
    return
  }

  teamLists.value = []
  teamListsCurrentPage.value = 1
  teamListsSearchStr.value = searchStr
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []
  getTeamLists()
}, 300)

const onChangeTab = async (tab: 'users' | 'team_lists') => {
  activeTab.value = tab

  if (tab === 'users' && !users.value.length) {
    await getUsers()
  }

  if (tab === 'team_lists' && !teamLists.value.length) {
    await getTeamLists()
  }
}

const handleCaretPosition = () => {
  if (!props.canAddComment) {
    menuOpen.value = false
    mentionQuery.value = ''
    activeMentionRange.value = null
    return
  }

  if (!isFocused.value) {
    if (isMenuInteracting.value) {
      return
    }

    menuOpen.value = false
    mentionQuery.value = ''
    activeMentionRange.value = null
    return
  }

  syncMessageFromDom()

  const mentionContext = getCurrentMentionContext()

  if (!mentionContext) {
    if (isMenuInteracting.value) {
      return
    }

    menuOpen.value = false
    mentionQuery.value = ''
    activeMentionRange.value = null
    return
  }

  activeMentionRange.value = mentionContext.range.cloneRange()
  mentionQuery.value = mentionContext.query
  menuOpen.value = true

  if (activeTab.value === 'users') {
    handleUserSearch(mentionContext.query)
  }
}

const ensureActiveMentionRange = (): Range | null => {
  if (activeMentionRange.value) {
    return activeMentionRange.value.cloneRange()
  }

  const mentionContext = getCurrentMentionContext()
  if (!mentionContext) {
    return null
  }

  activeMentionRange.value = mentionContext.range.cloneRange()
  return mentionContext.range.cloneRange()
}

const insertTaggedUsersAtMention = async (usersToInsert: TagListUser[]) => {
  if (!editableDiv.value || !usersToInsert.length) {
    return
  }

  const range = ensureActiveMentionRange()
  if (!range) {
    return
  }

  const fragment = document.createDocumentFragment()

  usersToInsert.forEach((user, index) => {
    const span = document.createElement('span')
    span.className = 'tagged-user-in-comment'
    span.setAttribute('data-value', String(user.id))
    span.setAttribute('contenteditable', 'false')
    span.textContent = user.display_name.replace(/<[^>]*>/g, '').trim()

    fragment.appendChild(span)

    const spaceNode = document.createTextNode(' ')
    fragment.appendChild(spaceNode)

    if (index === usersToInsert.length - 1) {
      setTimeout(() => {
        setCaretAt(spaceNode, 1)
      }, 0)
    }
  })

  range.deleteContents()
  range.insertNode(fragment)

  selectedUsersFullList.value = uniqBy(
    [...selectedUsersFullList.value, ...usersToInsert],
    'id'
  )

  syncMessageFromDom()
  menuOpen.value = false
  mentionQuery.value = ''
  activeMentionRange.value = null
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []

  await focusEditable()

  const selection = window.getSelection()
  if (selection?.rangeCount) {
    syncMessageFromDom()
  }
}

const selectUser = async (user: TagListUser) => {
  await insertTaggedUsersAtMention([user])
}

const onSelectTeamList = (teamList: TeamListItem) => {
  selectedTeamList.value = teamList
  selectedTeamListUserIds.value = teamList.users_data.map(user => user.id)
}

const toggleSelectAllTeamListUsers = () => {
  const ids = selectedTeamListUsers.value.map(user => user.id)

  if (!ids.length) {
    selectedTeamListUserIds.value = []
    return
  }

  if (isAllSelectedInTeamList.value) {
    selectedTeamListUserIds.value = []
    return
  }

  selectedTeamListUserIds.value = [...ids]
}

const onToggleTeamListUser = (userId: number, checked: boolean) => {
  if (checked) {
    selectedTeamListUserIds.value = uniqBy(
      [
        ...selectedTeamListUserIds.value.map(id => ({ id })),
        { id: userId },
      ],
      'id'
    ).map(item => item.id)

    return
  }

  selectedTeamListUserIds.value = selectedTeamListUserIds.value.filter(id => id !== userId)
}

const addSelectedUsersFromTeamList = async () => {
  if (!selectedTeamList.value || !selectedTeamListUserIds.value.length) {
    return
  }

  const selectedUsers = selectedTeamList.value.users_data.filter(user =>
    selectedTeamListUserIds.value.includes(user.id)
  )

  if (!selectedUsers.length) {
    return
  }

  await insertTaggedUsersAtMention(selectedUsers)
}

const clearEditor = () => {
  message.value = ''
  mentionQuery.value = ''
  menuOpen.value = false
  activeMentionRange.value = null
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []

  if (editableDiv.value) {
    editableDiv.value.innerHTML = ''
  }
}

const parseNodeToPayload = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '')
      .replace(/\u200B/g, '')
      .replace(/\u00A0/g, ' ')
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }

  const element = node as HTMLElement

  if (element.tagName === 'BR') {
    return '<br>'
  }

  if (element.tagName === 'SPAN' && element.classList.contains('tagged-user-in-comment')) {
    const dataValue = element.getAttribute('data-value')
    return dataValue ? `@${dataValue}` : ''
  }

  return Array.from(element.childNodes).map(parseNodeToPayload).join('')
}

const parseInnerHTMLToPayload = (): string => {
  if (!editableDiv.value) {
    return ''
  }

  let result = Array.from(editableDiv.value.childNodes).map(parseNodeToPayload).join('')

  result = result
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?<br> ?/g, '<br>')
    .replace(/(<br>){3,}/g, '<br><br>')
    .replace(/^(<br>)+|(<br>)+$/g, '')
    .trim()

  return result
}

const getChatMessageCreatePayload = (content: string, customItemId?: number): ChatMessage => {
  const payload = {
    content,
    message_type: MESSAGE_TYPE_MAP[props.chatItemType],
  } as ChatMessage

  const selectedChatItemId = customItemId ?? (
    Array.isArray(props.chatItemId)
      ? props.chatItemId[0]
      : props.chatItemId
  )

  const foreignKey = FOREIGN_KEY_MAP[props.chatItemType as keyof typeof FOREIGN_KEY_MAP]

  if (props.chatItemType !== 'general' && foreignKey) {
    payload[foreignKey] = selectedChatItemId as never
  }

  return payload
}

const addComment = async () => {
  const hasChatTarget = Array.isArray(props.chatItemId)
    ? !!props.chatItemId.length
    : !!props.chatItemId

  if (inputType.value === 'globalChat' && !hasChatTarget) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again. Item ID not found.',
    })
    return
  }

  if (inputType.value === 'sign' && !props.signId) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again. Sign ID not found.',
    })
    return
  }

  const parsedContent = parseInnerHTMLToPayload()

  if (!parsedContent) {
    return
  }

  addBtnLoading.value = true
  let isSuccess = false

  try {
    if (inputType.value === 'globalChat') {
      if (['wayfinding', 'branding', 'kop'].includes(props.chatItemType) && Array.isArray(props.chatItemId)) {
        const messageTypeMap: Record<'wayfinding' | 'branding' | 'kop', number> = {
          wayfinding: 2,
          branding: 3,
          kop: 4,
        }

        await $bulkCreateChatMessages({
          content: parsedContent,
          message_type: messageTypeMap[props.chatItemType as 'wayfinding' | 'branding' | 'kop'],
          item_ids: props.chatItemId,
        })

        const itemLabelMap = {
          wayfinding: 'sign(s)',
          branding: 'item(s)',
          kop: 'item(s)',
        } as const

        const itemLabel = itemLabelMap[props.chatItemType as keyof typeof itemLabelMap] || 'item(s)'

        snackbarStore.showMessage({
          color: 'success',
          icon: 'mdi-checkbox-marked-circle',
          title: 'Success',
          text: `Message sent to ${props.chatItemId.length} ${itemLabel} successfully!`,
        })
      }
      else {
        const chatMessageCreatePayload = getChatMessageCreatePayload(parsedContent)
        await $createChatMessage(chatMessageCreatePayload)

        snackbarStore.showMessage({
          color: 'success',
          icon: 'mdi-checkbox-marked-circle',
          title: 'Success',
          text: 'Message sent successfully!',
        })
      }
    }
    else if (['cr', 'crSubtask', 'crApproval'].includes(inputType.value)) {
      if (props.changeRequestId) {
        await $createChangeRequestComment({
          cr: props.changeRequestId,
          comment: parsedContent,
        })
      }

      if (props.changeRequestApprovalId) {
        await $createChangeRequestApprovalComment({
          cr_approval: props.changeRequestApprovalId,
          comment: parsedContent,
        })
      }

      if (props.changeRequestSubtaskId) {
        await $createChangeRequestSubtaskComment({
          cr_subtask: props.changeRequestSubtaskId,
          comment: parsedContent,
        })
      }

      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'Comment added successfully!',
      })
    }
    else {
      await $createSignComment({
        sign: props.signId,
        comment: parsedContent,
      })

      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'Sign comment added successfully!',
      })
    }

    isSuccess = true
  }
  catch (e: any) {
    console.log(e)

    const errorData = JSON.parse(JSON.stringify(e?.response?.data || {}))
    const firstErrorKey = Object.keys(errorData)[0]
    const errorText = firstErrorKey ? errorData[firstErrorKey] : 'Unexpected error.'

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong, try again. ${errorText}`,
    })
  }
  finally {
    addBtnLoading.value = false

    if (isSuccess) {
      clearEditor()
      emit('onAddComment')
    }
  }
}

const insertTextAtCursor = (text: string) => {
  const range = getSelectionRange()
  if (!range) {
    return
  }

  range.deleteContents()

  const lines = text.split('\n')
  const fragment = document.createDocumentFragment()
  let lastInsertedNode: Node | null = null

  lines.forEach((line, index) => {
    if (index > 0) {
      const br = document.createElement('br')
      fragment.appendChild(br)
      lastInsertedNode = br
    }

    const textNode = document.createTextNode(line)
    fragment.appendChild(textNode)
    lastInsertedNode = textNode
  })

  range.insertNode(fragment)

  if (lastInsertedNode) {
    if (lastInsertedNode.nodeType === Node.TEXT_NODE) {
      const textNode = lastInsertedNode as Text
      setCaretAt(textNode, textNode.textContent?.length || 0)
    }
    else {
      setCaretAfterNode(lastInsertedNode)
    }
  }

  syncMessageFromDom()
  handleCaretPosition()
}

const cleanPastedText = (text: string): string => {
  return text
    // Normalize line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Remove consecutive line breaks (keep max 2)
    .replace(/\n{3,}/g, '\n\n')
    // Normalize spaces (keep single spaces)
    .replace(/[ \t]+/g, ' ')
    // Remove special invisible characters (except zero-width space)
    .replace(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F\u007F-\u009F]/g, '')
    // Trim each line
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    // Final trim
    .trim()
}

const stripHtml = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent?.trim() || ''
  }
  catch (error) {
    return html.replace(/<[^>]*>/g, '').trim()
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()

  let pastedText = ''

  if (event.clipboardData?.types.includes('text/plain')) {
    pastedText = event.clipboardData.getData('text/plain')
  }
  else if (event.clipboardData?.types.includes('text/html')) {
    pastedText = stripHtml(event.clipboardData.getData('text/html'))
  }

  if (!pastedText) {
    return
  }

  const cleanedText = cleanPastedText(pastedText)
  insertTextAtCursor(cleanedText)
}

const handleInput = () => {
  syncMessageFromDom()
  handleCaretPosition()
}

const onMenuMouseDown = () => {
  isMenuInteracting.value = true
}

const onWindowMouseUp = () => {
  isMenuInteracting.value = false
}

const onEditorFocusOut = () => {
  requestAnimationFrame(() => {
    if (!isMenuInteracting.value) {
      isFocused.value = false
    }
  })
}

const handleOutsideClick = (event: MouseEvent) => {
  const target = event.target as Node | null

  if (!target || !rootRef.value?.contains(target)) {
    menuOpen.value = false
    activeMentionRange.value = null
    selectedTeamList.value = null
    selectedTeamListUserIds.value = []
    // isFocused.value = false - here is always false due to the @ actions
    isMenuInteracting.value = false
  } else {
    isMenuInteracting.value = false
  }
}

watch(() => JSON.stringify(getWaitingRoomTagFilters()), () => {
  menuOpen.value = false
  resetUsersState()
  resetTeamListsState()
  selectedUsersFullList.value = []
})

watch(menuOpen, async isOpen => {
  if (!isOpen) {
    return
  }

  if (Object.keys(getWaitingRoomTagFilters()).length) {
    resetUsersState()
    resetTeamListsState()
  }

  if (activeTab.value === 'users' && !users.value.length) {
    resetUsersState()
    usersSearchStr.value = mentionQuery.value
    await getUsers()
  }

  if (activeTab.value === 'team_lists' && !teamLists.value.length) {
    resetTeamListsState()
    await getTeamLists()
  }
})

watch(activeTab, async tab => {
  if (!menuOpen.value) {
    return
  }

  if (tab === 'users' && !users.value.length) {
    usersSearchStr.value = mentionQuery.value
    await getUsers()
  }

  if (tab === 'team_lists' && !teamLists.value.length) {
    await getTeamLists()
  }
})

onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
  document.addEventListener('selectionchange', handleCaretPosition)
  window.addEventListener('mouseup', onWindowMouseUp)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('selectionchange', handleCaretPosition)
  window.removeEventListener('mouseup', onWindowMouseUp)
})
</script>

<template>
  <div
    id="sign-comment-input-block"
    ref="rootRef"
  >
    <VCard
      v-if="menuOpen"
      class="comment-tag-menu mb-2"
      elevation="6"
      border
      @mousedown.stop="onMenuMouseDown"
    >
      <div class="pa-3 pb-2">
        <VTabs
          :model-value="activeTab"
          color="primary"
          density="compact"
          @update:modelValue="value => onChangeTab(value)"
        >
          <VTab value="users">
            Users
          </VTab>

<!--          // TODO: Uncomment after Team Lists integration-->
<!--          <VTab-->
<!--            v-if="withTeamLists"-->
<!--            value="team_lists"-->
<!--          >-->
<!--            Team Lists-->
<!--          </VTab>-->
        </VTabs>
      </div>

      <VDivider />

      <template v-if="activeTab === 'users'">
        <VList
          dense
          max-height="340"
        >
          <VListItem
            v-for="user in users"
            :key="user.id"
            :disabled="!!usersLoading || !!addBtnLoading"
            @mousedown.prevent
            @click.stop="selectUser(user)"
          >
            <template #title>
              {{ user.display_name }}
            </template>

            <template #subtitle>
              {{ user.email }}
            </template>
          </VListItem>

          <VListItem
            v-if="isNextUsersPageExist"
            :disabled="!!usersLoading || !!addBtnLoading"
            @mousedown.prevent
            @click="onLoadMoreUsers"
          >
            Load more...
            <VProgressCircular
              v-if="!!usersLoading"
              class="ml-2"
              size="16"
              width="3"
              indeterminate
              color="white"
            />
          </VListItem>

          <VListItem v-if="!users.length">
            <VOverlay
              v-model="usersLoading"
              persistent
              absolute
              contained
              class="align-center justify-center"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </VOverlay>
            Users not found
          </VListItem>
        </VList>
      </template>
<!--      // TODO: Uncomment after Team Lists integration-->
<!--      <template v-else-if="activeTab === 'team_lists'">-->
<!--        <div-->
<!--          class="pa-3 team-lists-panel"-->
<!--          @mousedown.stop="onMenuMouseDown"-->
<!--        >-->
<!--          <VTextField-->
<!--            :model-value="teamListsSearchStr"-->
<!--            label="Search Team Lists"-->
<!--            density="compact"-->
<!--            clearable-->
<!--            hide-details-->
<!--            @mousedown.stop="onMenuMouseDown"-->
<!--            @focus.stop="onMenuMouseDown"-->
<!--            @input.stop="onMenuMouseDown"-->
<!--            @update:modelValue="value => handleTeamListsSearch(String(value ?? ''))"-->
<!--          />-->

<!--          <div class="mt-3 d-flex team-lists-content">-->
<!--            <div class="team-lists-column">-->
<!--              <div class="text-subtitle-1 mb-1">-->
<!--                Team Lists-->
<!--              </div>-->

<!--              <VList-->
<!--                border-->
<!--                rounded="lg"-->
<!--                class="py-0"-->
<!--                max-height="340"-->
<!--                style="overflow-y: auto;"-->
<!--              >-->
<!--                <VListItem-->
<!--                  v-for="teamList in teamLists"-->
<!--                  :key="teamList.id"-->
<!--                  :active="selectedTeamList?.id === teamList.id"-->
<!--                  @mousedown.prevent-->
<!--                  @click="onSelectTeamList(teamList)"-->
<!--                >-->
<!--                  <template #title>-->
<!--                    {{ teamList.name }}-->
<!--                  </template>-->

<!--                  <template #subtitle>-->
<!--                    {{ teamList.users_data?.length || 0 }} users-->
<!--                  </template>-->
<!--                </VListItem>-->

<!--                <div-->
<!--                  v-if="teamListsLoading"-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  <VProgressCircular indeterminate size="22" />-->
<!--                </div>-->

<!--                <div-->
<!--                  v-else-if="!teamLists.length"-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  Team Lists not found-->
<!--                </div>-->
<!--              </VList>-->

<!--              <VBtn-->
<!--                v-if="teamLists.length < teamListsCount"-->
<!--                class="mt-1 py-1"-->
<!--                size=""-->
<!--                block-->
<!--                variant="tonal"-->
<!--                color="secondary"-->
<!--                :loading="teamListsLoading"-->
<!--                @mousedown.prevent-->
<!--                @click="onLoadMoreTeamLists"-->
<!--              >-->
<!--                Load more Team Lists-->
<!--              </VBtn>-->
<!--            </div>-->

<!--            <div class="team-users-column">-->
<!--              <div class="d-flex align-center justify-space-between mb-1">-->
<!--                <div class="text-subtitle-2">-->
<!--                  {{-->
<!--                    selectedTeamList-->
<!--                      ? `Users from "${selectedTeamList.name}"`-->
<!--                      : 'Select Team List'-->
<!--                  }}-->
<!--                </div>-->

<!--                <div-->
<!--                  v-if="selectedTeamList"-->
<!--                  class="d-flex align-center"-->
<!--                  style="gap: 8px;"-->
<!--                >-->
<!--                  <VCheckbox-->
<!--                    :model-value="isAllSelectedInTeamList"-->
<!--                    :indeterminate="isIndeterminateSelectedInTeamList"-->
<!--                    hide-details-->
<!--                    density="compact"-->
<!--                    label="Select all"-->
<!--                    @update:modelValue="toggleSelectAllTeamListUsers"-->
<!--                  />-->

<!--                  <VBtn-->
<!--                    size=""-->
<!--                    color="primary"-->
<!--                    class="pa-1"-->
<!--                    :disabled="!selectedTeamListUserIds.length"-->
<!--                    @mousedown.prevent-->
<!--                    @click="addSelectedUsersFromTeamList"-->
<!--                  >-->
<!--                    Add selected-->
<!--                    <span v-if="selectedTeamListUserIds.length">-->
<!--                      {{ ` (${selectedTeamListUserIds.length})` }}-->
<!--                    </span>-->
<!--                  </VBtn>-->
<!--                </div>-->
<!--              </div>-->

<!--              <VList-->
<!--                border-->
<!--                rounded="lg"-->
<!--                class="py-0"-->
<!--                max-height="340"-->
<!--                style="overflow-y: auto;"-->
<!--              >-->
<!--                <template v-if="selectedTeamList">-->
<!--                  <VListItem-->
<!--                    v-for="user in selectedTeamListUsers"-->
<!--                    :key="user.id"-->
<!--                  >-->
<!--                    <template #prepend>-->
<!--                      <VCheckbox-->
<!--                        :model-value="selectedTeamListUserIds.includes(user.id)"-->
<!--                        hide-details-->
<!--                        density="compact"-->
<!--                        @update:modelValue="checked => onToggleTeamListUser(user.id, !!checked)"-->
<!--                      />-->
<!--                    </template>-->

<!--                    <template #title>-->
<!--                      {{ user.display_name }}-->
<!--                    </template>-->

<!--                    <template #subtitle>-->
<!--                      {{ user.email }}-->
<!--                    </template>-->
<!--                  </VListItem>-->

<!--                  <div-->
<!--                    v-if="!selectedTeamListUsers.length"-->
<!--                    class="pa-4 text-center"-->
<!--                  >-->
<!--                    No users in this Team List-->
<!--                  </div>-->
<!--                </template>-->

<!--                <div-->
<!--                  v-else-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  Select Team List to view users-->
<!--                </div>-->
<!--              </VList>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </template>-->
    </VCard>

    <div
      class="custom-textarea"
      :class="{ 'disabled-div': !canAddComment }"
    >
      <div class="prepend-inner">
        <div>
          <VIcon
            size="small"
            icon="tabler-info-circle"
          />
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="5"
            class="default-tooltip"
          >
            <span class="px-2">
              Use @ + Select User to tag user(s)
            </span>
          </VMenu>
        </div>
      </div>

      <div class="comment-input-container">
        <div
          :id="customMessageInputId"
          ref="editableDiv"
          :contenteditable="canAddComment ? 'true' : 'false'"
          @input="handleInput"
          @keydown="handleKeyDown"
          @focusin="isFocused = true"
          @focusout="onEditorFocusOut"
          @paste="handlePaste"
        />

        <div
          v-if="!message && !isFocused"
          class="placeholder-block"
          @click="focusEditable"
        >
          {{ placeholder }}
        </div>
      </div>

      <div class="append-inner">
        <div>
          <VBtn
            size=""
            icon="tabler-send-2"
            class="rounded pa-1"
            color="light"
            variant="text"
            :disabled="!message.trim() || !!addBtnLoading || !canAddComment"
            @click="addComment"
          >
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-1">
                Add
              </span>
            </VMenu>

            <div class="position-relative">
              <VProgressCircular
                v-if="!!addBtnLoading"
                class="add-comment-btn-loader"
                size="24"
                width="2"
                indeterminate
                color="primary"
              />
              <VIcon
                :color="!message.trim() ? 'light' : 'primary'"
                icon="tabler-send-2"
              />
            </div>
          </VBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.comment-tag-menu {
  position: relative;
  z-index: 20;
  max-width: 860px;
}

.team-lists-panel {
  min-width: 720px;
}

.team-lists-content {
  gap: 12px;
  min-height: 360px;
}

.team-lists-column {
  width: 42%;
  min-width: 260px;
}

.team-users-column {
  width: 58%;
}

.custom-textarea {
  position: relative;

  .prepend-inner {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 5px;
  }

  .append-inner {
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 5px;
  }

  .placeholder-block {
    position: absolute;
    top: 5px;
    left: 40px;
    color: #aaa;
    cursor: text;
  }

  div[contenteditable=true],
  div[contenteditable='true'] {
    border: 1px solid #ccc;
    padding: 5px;
    min-height: 42.5px;
    margin-left: 35px;
    margin-right: 35px;
    transition: border-color 0.3s ease;
    border-radius: 5px;
    white-space: pre-wrap;
    word-break: break-word;

    &:focus {
      outline: none;
      border-color: rgb(var(--v-theme-primary));
      border-radius: 5px;
    }

    .tagged-user-in-comment {
      color: rgb(var(--v-theme-primary)) !important;
      text-decoration: underline;
    }
  }

  .tagged-user-in-comment {
    color: rgb(var(--v-theme-primary)) !important;
    text-decoration: underline;
  }
}

.disabled-div {
  pointer-events: none;
  opacity: 0.6;
}
</style>
